import React, { useEffect, useRef, useState } from "react";
import { Bell, X, Check, CheckCheck, Inbox, Clock, Calendar as CalendarIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const KIND_ICON = {
  lead_stuck_new: Inbox,
  lead_stuck_contacted: Clock,
  appointment_missed: CalendarIcon,
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "adesso";
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  const d = Math.floor(h / 24);
  return `${d}g fa`;
};

export const NotificationsBell = ({ variant = "desktop" }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);

  const fetchCount = () => {
    api.get("/crm/notifications/unread-count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  };
  const fetchAll = () => {
    setLoading(true);
    api.get("/crm/notifications")
      .then((r) => { setItems(r.data.items || []); setUnread(r.data.unread || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 60000); // poll every 60s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) fetchAll();
  }, [open]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markAll = async () => {
    await api.post("/crm/notifications/mark-read", { ids: null });
    fetchAll();
  };
  const markOne = async (id) => {
    await api.post("/crm/notifications/mark-read", { ids: [id] });
    setItems((x) => x.map((n) => n.id === id ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
  };
  const go = (n) => {
    if (!n.read) markOne(n.id);
    setOpen(false);
    if (n.link) nav(n.link);
  };

  return (
    <div className="relative" ref={ref} data-testid={`notifications-bell-${variant}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 flex items-center justify-center border border-[#2A2A2A] text-[#FAFAF7] hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition"
        data-testid={`notifications-bell-btn-${variant}`}
        aria-label="Notifiche"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#FF5A1F] text-[#FAFAF7] text-[10px] font-bold border-2 border-[#0A0A0A]" data-testid="notifications-unread-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-[#FAFAF7] text-[#0A0A0A] border-2 border-[#0A0A0A] shadow-2xl z-50" data-testid="notifications-panel">
          <div className="p-3 border-b-2 border-[#0A0A0A] flex items-center justify-between bg-[#0A0A0A] text-[#FAFAF7]">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">Notifiche</div>
              <div className="text-xs">{unread} da leggere</div>
            </div>
            {unread > 0 && (
              <button onClick={markAll} className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F] hover:underline flex items-center gap-1" data-testid="notifications-mark-all">
                <CheckCheck size={12} /> Segna tutte lette
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-[#0A0A0A]/50 text-sm">Caricamento…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-[#0A0A0A]/40 text-sm" data-testid="notifications-empty">
                Nessuna notifica.
              </div>
            ) : (
              items.map((n) => {
                const Icon = KIND_ICON[n.kind] || Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => go(n)}
                    className={`w-full text-left flex gap-3 p-3 border-b border-[#0A0A0A]/10 hover:bg-[#F2F2EC] transition ${n.read ? "" : "bg-[#FF5A1F]/5"}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className={`shrink-0 w-8 h-8 flex items-center justify-center border-2 ${n.read ? "border-[#0A0A0A]/20 text-[#0A0A0A]/40" : "border-[#FF5A1F] text-[#FF5A1F]"}`}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{n.title}</div>
                      <div className="text-xs text-[#0A0A0A]/70 line-clamp-2 mt-0.5">{n.body}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/40 mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                    {!n.read && <div className="w-2 h-2 bg-[#FF5A1F] rounded-full shrink-0 mt-1.5" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
